import React, {useContext} from "react";
import FlexBox from "view/components/Layout/FlexBox";
import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
} from "@material-ui/core";
import AlertIcon from "view/images/AlertIcon";
import {Controller, UseFormReturn} from "react-hook-form";
import {FormContext} from "view/components/Form";
import {TextResponseCell} from "ts/api/task";
import {CellType} from "view/components/GridResponseBlock/types";
import type {TaskResponseUpdate} from "view/components/TasksList/types";
import clsx from "clsx";
import {T} from "@tolgee/react";
import useStyles from "./styles";

type TextInputProps = {
  isRequired: boolean;
  viewOnly?: boolean;
  options: string[];
  isJustSavedDraft?: React.MutableRefObject<boolean>;
  columnIndex: number;
  rowIndex: number;
};
const defaultValue = {type: CellType.Text, text: ""} as TextResponseCell;

function OptionsResponseInput({
  isRequired,
  viewOnly,
  options,
  isJustSavedDraft,
  columnIndex,
  rowIndex,
}: TextInputProps) {
  const classes = useStyles();
  const theme = useTheme();
  const form = useContext(FormContext) as UseFormReturn<
    Pick<TaskResponseUpdate, "grid">
  >;

  const {control, formState} = form;

  const isError =
    formState?.errors?.grid?.[rowIndex]?.cells?.[columnIndex]?.type ===
    "required";

  return (
    <FlexBox alignCenter gap={1} width="100%">
      <FlexBox flex={1} minWidth={0} justifyContent="space-between" alignCenter>
        <FormControl
          fullWidth
          variant="outlined"
          className={classes.cellInputText}
        >
          {!viewOnly && (
            <InputLabel
              className={classes.selectPlaceholder}
              classes={{shrink: classes.selectPlaceholderShrink}}
            >
              <T keyName="common.tasks.grid.cell.select_option" />
            </InputLabel>
          )}
          <Controller
            name={`grid.${rowIndex}.cells.${columnIndex}`}
            defaultValue={defaultValue}
            control={control}
            disabled={viewOnly}
            rules={{
              validate: {
                required: (value) =>
                  !isRequired ||
                  (value?.type === "text" && !!value?.text?.trim()?.length),
              },
            }}
            render={({field}) => {
              const selectedText =
                (field.value as TextResponseCell)?.text || "";

              // View mode: render the submitted option as wrapping text so the
              // organiser can read the full value. A disabled Select clips long
              // text at its fixed height and can't be opened. Mirrors the
              // auto-growing behaviour of the text-response cell.
              if (viewOnly) {
                return (
                  <div className={classes.optionViewValue}>{selectedText}</div>
                );
              }

              return (
                <Select
                  style={{border: "none"}}
                  className={clsx(
                    classes.cellInput,
                    isError && classes.errorField,
                    classes.optionsResponseInput
                  )}
                  MenuProps={{
                    PaperProps: {
                      className: classes.selectMenu,
                    },
                  }}
                  {...field}
                  value={selectedText}
                  onChange={(e) => {
                    const text = e.target.value as string;
                    const newVal: TextResponseCell = {
                      type: CellType.Text,
                      text,
                    };
                    if (isJustSavedDraft?.current) {
                      isJustSavedDraft.current = false;
                    }
                    return field.onChange(newVal);
                  }}
                >
                  {options.map((option, index) => (
                    <MenuItem
                      className={classes.menuItem}
                      key={index}
                      value={option}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              );
            }}
          />
        </FormControl>
      </FlexBox>
      {isRequired && (
        <FlexBox alignCenter gap={0.75} flexShrink={0}>
          <IconButton disabled className={classes.cellButton}>
            <AlertIcon fill={isError ? theme.palette.error.main : undefined} />
          </IconButton>
        </FlexBox>
      )}
    </FlexBox>
  );
}

export default OptionsResponseInput;
